import { TICK_DURATION } from "@/config/constants";
import type { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import type {
	EventMilestoneConfig,
	EventMilestoneProgressValue,
	EventMilestoneRewardConfig,
} from "@/types";
import {
	MilestoneRewardType,
	MilestoneStatus,
	type MilestoneInGameRewardBuilding,
	type MilestoneInGameRewardCurrency,
	type MilestoneItem,
} from "@/types/event";

const ticksPerMinute = 60 / TICK_DURATION;

const resolveCurrencyType = (
	reward: EventMilestoneRewardConfig,
): ResourceType | null => {
	if (reward.type !== "currency") return null;

	const rawCurrency = reward.currency;
	if (!rawCurrency) return null;

	const isKnownCurrency = Object.values(ResourceType).includes(
		rawCurrency as ResourceType,
	);
	return isKnownCurrency ? (rawCurrency as ResourceType) : null;
};

const toCurrencyReward = (
	reward: EventMilestoneRewardConfig,
	coinsIncome: number,
): MilestoneInGameRewardCurrency | null => {
	if (reward.type !== "currency") return null;

	const currency = resolveCurrencyType(reward);
	if (!currency) return null;

	const fixedAmount = reward.amount ?? 0;
	const dynamicAmount =
		typeof reward.incomeMinutes === "number" && reward.incomeMinutes > 0
			? coinsIncome * ticksPerMinute * reward.incomeMinutes
			: 0;

	const amount = Math.max(0, fixedAmount, dynamicAmount);

	return {
		type: MilestoneRewardType.CURRENCY,
		currency,
		amount,
	};
};

const toBuildingReward = (
	reward: EventMilestoneRewardConfig,
): MilestoneInGameRewardBuilding | null => {
	if (reward.type !== "item") return null;

	return {
		type: MilestoneRewardType.ITEM,
		frameName: reward.frameName as FrameName,
		amount: reward.amount,
	};
};

const toInGameReward = (
	reward: EventMilestoneRewardConfig,
	coinsIncome: number,
): MilestoneInGameRewardCurrency | MilestoneInGameRewardBuilding | null => {
	if (reward.type === "currency") {
		return toCurrencyReward(reward, coinsIncome);
	}

	return toBuildingReward(reward);
};

export const buildMilestoneMapFromEventConfig = (
	milestones: EventMilestoneConfig[],
	coinsIncome: number,
): Map<number, MilestoneItem> | null => {
	if (!milestones.length) return null;

	const mappedMilestones = new Map<number, MilestoneItem>();

	milestones.forEach((milestone, index) => {
		const reward = toInGameReward(milestone.reward, coinsIncome);
		if (!reward) return;

		const mappedMilestone: MilestoneItem = {
			requiredSum: milestone.requiredSum,
			reward,
		};

		if (milestone.premium?.reward) {
			const premiumReward = toInGameReward(
				milestone.premium.reward,
				coinsIncome,
			);

			if (premiumReward) {
				mappedMilestone.premium = {
					reward: premiumReward,
				};
			}
		}

		mappedMilestones.set(index + 1, mappedMilestone);
	});

	return mappedMilestones.size ? mappedMilestones : null;
};

function isFreeBranchClaimed(
	claimed: Record<number, EventMilestoneProgressValue> | null,
	id: number,
): boolean {
	if (!claimed?.[id]) return false;
	const progress = claimed[id];
	return typeof progress === "boolean" || !!(progress && progress.free);
}

function isPremiumBranchClaimed(
	claimed: Record<number, EventMilestoneProgressValue> | null,
	id: number,
): boolean {
	const progress = claimed?.[id];
	if (!progress || typeof progress === "boolean") return false;
	return !!progress.premium;
}

function deriveFreeStatus(
	config: MilestoneItem,
	claimed: Record<number, EventMilestoneProgressValue> | null,
	id: number,
	eventiumAccumulated: number,
): MilestoneStatus {
	if (isFreeBranchClaimed(claimed, id)) {
		return MilestoneStatus.CLAIMED;
	}
	return config.requiredSum <= eventiumAccumulated
		? MilestoneStatus.CLAIMABLE
		: MilestoneStatus.LOCKED;
}

function derivePremiumStatus(
	config: MilestoneItem,
	claimed: Record<number, EventMilestoneProgressValue> | null,
	id: number,
	eventiumAccumulated: number,
	eventPassPurchased: boolean,
): MilestoneStatus {
	if (!config.premium) return MilestoneStatus.LOCKED;
	if (isPremiumBranchClaimed(claimed, id)) {
		return MilestoneStatus.CLAIMED;
	}
	if (config.requiredSum > eventiumAccumulated) {
		return MilestoneStatus.LOCKED;
	}
	return eventPassPurchased
		? MilestoneStatus.CLAIMABLE
		: MilestoneStatus.LOCKED;
}

/** Attach free/premium claim status to config-only milestone map. */
export function deriveMilestoneStatuses(
	configById: Map<number, MilestoneItem>,
	claimed: Record<number, EventMilestoneProgressValue> | null,
	eventiumAccumulated: number,
	eventPassPurchased: boolean,
): Map<number, MilestoneItem> {
	const out = new Map<number, MilestoneItem>();

	for (const [id, config] of configById) {
		const milestoneItem: MilestoneItem = {
			requiredSum: config.requiredSum,
			reward: config.reward,
			status: deriveFreeStatus(config, claimed, id, eventiumAccumulated),
		};

		if (config.premium) {
			milestoneItem.premium = {
				reward: config.premium.reward,
				status: derivePremiumStatus(
					config,
					claimed,
					id,
					eventiumAccumulated,
					eventPassPurchased,
				),
			};
		}

		out.set(id, milestoneItem);
	}

	return out;
}
